<template>
  <div class="pa-policies-section">
    <header class="pa-section-header">
      <div>
        <h2 class="pa-section-title">Health Insurance</h2>
        <p class="pa-section-subtitle">
          {{ policies.length }} active {{ policies.length === 1 ? 'policy' : 'policies' }}
        </p>
      </div>
    </header>

    <div v-if="loading" class="policy-shimmer" aria-hidden="true">
      <div v-for="n in 2" :key="`health-shimmer-${n}`" class="policy-shimmer-card">
        <div class="shimmer policy-shimmer-icon"></div>
        <div class="policy-shimmer-copy">
          <div class="shimmer policy-shimmer-line policy-shimmer-line--title"></div>
          <div class="shimmer policy-shimmer-line"></div>
        </div>
        <div class="shimmer policy-shimmer-block"></div>
      </div>
    </div>

    <div v-else-if="missingId" class="policy-product-empty">
      <v-icon size="40" color="#94a3b8">mdi-card-account-details-outline</v-icon>
      <p>Your national ID is not on this account, so health cover cannot be loaded.</p>
    </div>

    <div v-else-if="error" class="policy-product-empty">
      <v-icon size="40" color="#94a3b8">mdi-hospital-box-outline</v-icon>
      <p>{{ error }}</p>
      <button type="button" class="health-retry" @click="$emit('retry')">Try again</button>
    </div>

    <div v-else-if="policies.length === 0" class="policy-product-empty">
      <v-icon size="40" color="#94a3b8">mdi-hospital-box-outline</v-icon>
      <p>No health policies on this national ID.</p>
    </div>

    <template v-else>
    <v-card
      v-for="(policy, index) in policies"
      :id="cardId(policy)"
      :key="policy.policyNo || index"
      class="pa-policy-card pa-policy-card--health"
      flat
    >
      <div class="pa-policy-card__body">
        <div class="pa-policy-card__hero">
          <div class="pa-policy-card__icon health-policy-icon">
            <v-icon size="36" color="#184679">mdi-hospital-box-outline</v-icon>
          </div>
          <div class="pa-policy-card__intro">
            <div class="pa-policy-card__title-row">
              <h3 class="pa-policy-card__title">{{ productLabel(policy.productName) }}</h3>
              <span class="pa-policy-card__status">Active</span>
            </div>
            <p class="pa-policy-card__option">{{ memberLine(policy) }}</p>
          </div>
        </div>

        <div class="pa-policy-card__details">
          <div class="pa-detail-block">
            <span class="pa-detail-label">Policy number</span>
            <span class="pa-detail-value pa-detail-value--mono">{{ policy.policyNo || '—' }}</span>
          </div>
          <div class="pa-detail-block">
            <span class="pa-detail-label">Cover from</span>
            <span class="pa-detail-value">{{ formatCover(policy.coverStartDate) }}</span>
          </div>
          <div class="pa-detail-block pa-detail-block--premium">
            <span class="pa-detail-label">Premium</span>
            <span class="pa-detail-value pa-detail-value--premium">KES {{ formatKes(policy.premium) }}</span>
          </div>
        </div>

        <div class="pa-policy-card__actions">
          <button
            type="button"
            class="health-view-btn"
            :aria-expanded="isOpen(policy) ? 'true' : 'false'"
            @click="toggle(policy)"
          >
            {{ isOpen(policy) ? 'Hide cover' : 'View cover' }}
          </button>
        </div>

        <div v-if="isOpen(policy)" class="health-detail">
          <div class="health-people">
            <div
              v-for="member in policy.members || []"
              :key="member.memberNo"
              class="health-person"
            >
              <p class="health-person__name">{{ member.name || 'Member' }}</p>
              <p class="health-person__role">{{ memberRole(member) }}</p>
            </div>
          </div>
          <h4 class="health-detail__title">Cover limits</h4>
          <p v-if="!(policy.benefits || []).length" class="health-empty-benefits">No benefit limits listed.</p>
          <ul v-else class="health-schedule">
            <li
              v-for="(benefit, benefitIndex) in listedBenefits(policy)"
              :key="`${benefit.memberNo}-${benefitIndex}`"
              class="health-schedule__row"
              :class="{ 'health-schedule__row--main': isInpatient(benefit) }"
            >
              <span>
                {{ benefitTitle(benefit) }}
                <span v-if="(policy.members || []).length > 1" class="health-schedule__who">{{ memberName(policy, benefit.memberNo) }}</span>
              </span>
              <span>KES {{ formatKes(benefit.limit) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </v-card>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import {
  benefitTitle,
  benefitsForMember,
  formatKes,
  memberCountLabel,
  principalMember,
  productLabel,
} from '@/utils/healthPolicyDisplay';
import { policyFocusId } from '@/utils/policyRecency';

export default {
  name: 'HealthPolicySection',
  props: {
    policies: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    missingId: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openPolicyNo: null,
    };
  },
  methods: {
    benefitTitle,
    benefitsForMember,
    formatKes,
    principalMember,
    productLabel,
    cardId(policy) {
      return policyFocusId('health', policy);
    },
    memberLine(policy) {
      const principal = principalMember(policy);
      const name = principal?.name;
      const count = memberCountLabel(policy);
      return name ? `${name} · ${count}` : count;
    },
    memberRole(member) {
      const relationship = member.relationship
        ? member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)
        : 'Member';
      const dob = member.dateOfBirth ? moment(member.dateOfBirth).format('DD MMM YYYY') : '';
      return dob ? `${relationship} · ${dob}` : relationship;
    },
    formatCover(value) {
      if (!value) return '—';
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('DD MMM YYYY') : String(value);
    },
    listedBenefits(policy) {
      return (policy.members || []).reduce((rows, member) => (
        rows.concat(benefitsForMember(policy, member.memberNo))
      ), []);
    },
    isInpatient(benefit) {
      return benefitTitle(benefit).toLowerCase() === 'inpatient';
    },
    memberName(policy, memberNo) {
      const member = (policy.members || []).find((item) => item.memberNo === memberNo);
      return member?.name || memberNo;
    },
    isOpen(policy) {
      return this.openPolicyNo === (policy.policyNo || null);
    },
    toggle(policy) {
      const key = policy.policyNo || null;
      this.openPolicyNo = this.openPolicyNo === key ? null : key;
    },
  },
};
</script>

<style scoped>
.pa-policies-section {
  margin-top: 1rem;
}

.pa-section-header {
  margin-bottom: 1rem;
}

.pa-section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1E4276;
}

.pa-section-subtitle {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #64748B;
}

.policy-product-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
}

.pa-policy-card {
  border: 1px solid #E2E8F0 !important;
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 4px 18px rgba(24, 70, 121, 0.06) !important;
}

.pa-policy-card__body {
  padding: 1.25rem 1.25rem 1rem;
}

@media (min-width: 768px) {
  .pa-policy-card__body {
    padding: 1.5rem 1.75rem 1.25rem;
  }
}

.pa-policy-card__hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 1.25rem;
}

.pa-policy-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pa-policy-card__intro {
  flex: 1;
  min-width: 0;
}

.pa-policy-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pa-policy-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
}

.pa-policy-card__status {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #15803D;
  background: #DCFCE7;
  border-radius: 999px;
  padding: 3px 10px;
}

.pa-policy-card__option {
  margin: 4px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #00AEEF;
}

.pa-policy-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 14px 16px;
  background: #F8FAFC;
  border: 1px solid #EEF2F6;
  border-radius: 12px;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .pa-policy-card__details {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .pa-detail-block--premium {
    text-align: right;
  }
}

.pa-detail-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pa-detail-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94A3B8;
}

.pa-detail-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1E4276;
  line-height: 1.35;
}

.pa-detail-value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  word-break: break-all;
}

.pa-detail-value--premium {
  color: #184679;
  font-size: 1rem;
}

.policy-shimmer {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.policy-shimmer-card {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 14px 16px;
  padding: 1.25rem;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  background: #fff;
}

.policy-shimmer-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
}

.policy-shimmer-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}

.policy-shimmer-line {
  height: 12px;
  width: 46%;
  border-radius: 999px;
}

.policy-shimmer-line--title {
  width: 28%;
  height: 16px;
}

.policy-shimmer-block {
  grid-column: 1 / -1;
  height: 64px;
  border-radius: 12px;
}

.shimmer {
  background: linear-gradient(90deg, #e8eef5 25%, #f8fafc 37%, #e8eef5 63%);
  background-size: 400% 100%;
  animation: policy-shimmer 1.2s ease infinite;
}

@keyframes policy-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

.health-policy-icon {
  background: linear-gradient(135deg, #E0F4FD 0%, #F0F9FF 100%);
}

.health-view-btn,
.health-retry {
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  box-shadow: none;
  background: none;
  color: #184679;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

.health-view-btn:focus,
.health-view-btn:focus-visible,
.health-retry:focus,
.health-retry:focus-visible {
  outline: none;
  box-shadow: none;
  border: none;
}

.health-view-btn:hover,
.health-retry:hover {
  color: #0284C7;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.health-detail {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #E2E8F0;
}

.health-detail__title {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94A3B8;
}

.health-people {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.health-person__name {
  margin: 0;
  color: #1E4276;
  font-size: 0.95rem;
  font-weight: 800;
}

.health-person__role {
  margin: 4px 0 0;
  color: #64748B;
  font-size: 0.82rem;
  font-weight: 600;
}

.health-empty-benefits {
  margin: 0 0 1rem;
  color: #64748B;
  font-size: 0.9rem;
  font-weight: 600;
}

.health-schedule__who {
  color: #64748B;
  font-weight: 600;
}

.health-schedule {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
}

.health-schedule__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 14px;
  border-top: 1px solid #EEF2F6;
  color: #1E4276;
  font-size: 0.92rem;
  font-weight: 700;
}

.health-schedule__row:first-child {
  border-top: 0;
}

.health-schedule__row--main {
  background: #F0F9FF;
}

.health-schedule__row span:last-child {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.health-schedule__who {
  display: block;
  margin-top: 2px;
  font-size: 0.75rem;
}
</style>
